import { Dayjs } from 'dayjs';
import { LegacyCustomerOrderBase } from '../../contracts/customer-order.js';
import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../dto/data-model.js';
import { BusinessModuleRef } from './business-module-ref.js';
import { PaymentType } from './payment-type.js';
import { PaymentMethod } from './payment-method.js';
import { OnlinePaymentDetails } from './online-payment-details.js';
import { ComponentList } from '../../components/core/component-list.js';
import { Language } from './language.js';
import { Price } from './price.js';
import { OpenAccessPass } from './open-access-pass/open-access-pass.js';
import { MapUtils } from '../../utility/map-utils.js';
import { fromEnumAsString, fromEnumAsStringOrNull, toEnumFromString, toEnumFromStringOrNull } from '../../serialization/enum-utils.js';
import { Component } from '../../components/core/component.js';
import { TimeZone } from '../../time/timezone.js';
import { isSameData, isSameList } from '../dto/data-model-utils.js';
import { PaymentStatus } from './payment-status.js';
import { CustomerAccountBase } from './customer-account-base.js';
import { CustomerProfile } from './customer-profile.js';
import { InvoiceVat } from './invoicing/invoice-vat.js';
import { getCountryOfTimeZone, TimeZoneRegion } from './time-zone-region.js';
import { OrderSource } from './order-source.js';
import { Country } from './country.js';
import { VersionRange } from '../core/version-range.js';
import { OrderDomain } from './order-domain.js';
import { OfflinePaymentDetails } from './offline-payment-details.js';

export enum OnlineEventPassOrderStatus {
  Open = 'OPEN',
  Aborted = 'ABORTED',
  Canceled = 'CANCELED',
  Refunded = 'REFUNDED',
}

export class OnlineEventPassOrder extends DataModel implements LegacyCustomerOrderBase {
  static readonly TYPE_ID: string = '3bebfa5bd9564f96b0dbeec736bf5569';
  static readonly SCHEMA_VERSION: number = 4;

  public readonly isLegacyOrder = true;

  public readonly customerId: string | null;
  public readonly userId: string | null;
  public readonly language: Language | null;

  private constructor(
    public readonly uniqueId: string,
    public readonly creatorId: string,
    public readonly creationDate: Dayjs,
    public readonly businessRef: BusinessModuleRef,
    public readonly country: Country,
    public readonly timezone: TimeZoneRegion,
    public readonly status: OnlineEventPassOrderStatus,
    public readonly paymentType: PaymentType,
    public readonly paymentMethod: PaymentMethod,
    public readonly onlinePaymentDetails: OnlinePaymentDetails | null,
    public readonly offlinePaymentDetails: OfflinePaymentDetails | null,
    public readonly source: OrderSource,
    public readonly domain: OrderDomain,
    public readonly components: ComponentList,
    public readonly isGuestOrder: boolean,
    public readonly cancelledDate: Dayjs | null,
    public readonly cancelledBy: string | null,
    public readonly applicationFee: Price | null,
    public readonly applicationFeeVat: InvoiceVat | null,
    public readonly passes: OpenAccessPass[],
  ) {
    super();
    this.userId = passes[0].customer?.userId ?? null;
    this.customerId = passes[0].customer?.uniqueId ?? null;
    this.language = passes[0].customer?.language ?? null;
  }

  get $typeId(): string {
    return OnlineEventPassOrder.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, OnlineEventPassOrder.SCHEMA_VERSION);
  }

  get isOpen(): boolean {
    return this.status === OnlineEventPassOrderStatus.Open;
  }

  get isCanceled(): boolean {
    return this.status === OnlineEventPassOrderStatus.Canceled;
  }

  static create(opts: {
    uniqueId: string,
    creatorId: string,
    creationDate: Dayjs,
    businessRef: BusinessModuleRef,
    country: Country,
    timezone: TimeZoneRegion,
    status: OnlineEventPassOrderStatus,
    paymentType: PaymentType,
    paymentMethod: PaymentMethod,
    onlinePaymentDetails: OnlinePaymentDetails | null,
    offlinePaymentDetails: OfflinePaymentDetails | null,
    source: OrderSource,
    domain: OrderDomain,
    components: ComponentList,
    isGuestOrder: boolean,
    cancelledDate: Dayjs | null,
    cancelledBy: string | null,
    applicationFee: Price | null,
    applicationFeeVat: InvoiceVat | null,
    passes: OpenAccessPass[],
  }): OnlineEventPassOrder {
    return new OnlineEventPassOrder(
        opts.uniqueId,
        opts.creatorId,
        opts.creationDate,
        opts.businessRef,
        opts.country,
        opts.timezone,
        opts.status,
        opts.paymentType,
        opts.paymentMethod,
        opts.onlinePaymentDetails,
        opts.offlinePaymentDetails,
        opts.source,
        opts.domain,
        opts.components,
        opts.isGuestOrder,
        opts.cancelledDate,
        opts.cancelledBy,
        opts.applicationFee,
        opts.applicationFeeVat,
        opts.passes,
    );
  }


  static fromMap(context: SerializationContext, map: SerializedData): OnlineEventPassOrder {
    return new OnlineEventPassOrder(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.get<string>(map, 'creatorId'),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        BusinessModuleRef.mustDecode(MapUtils.get<string>(map, 'businessRef')),
        toEnumFromString(MapUtils.get<string>(map, 'country'), Country),
        toEnumFromString(MapUtils.get<string>(map, 'timezone'), TimeZoneRegion),
        toEnumFromString(MapUtils.get<string>(map, '$status'), OnlineEventPassOrderStatus),
        toEnumFromString(MapUtils.get<string>(map, 'paymentType'), PaymentType),
        toEnumFromString(MapUtils.get<string>(map, 'paymentMethod'), PaymentMethod),
        context.fromDataOrNull<OnlinePaymentDetails>(MapUtils.getOrNull<SerializedData>(map, 'onlinePaymentDetails')),
        context.fromDataOrNull<OfflinePaymentDetails>(MapUtils.getOrNull<SerializedData>(map, 'offlinePaymentDetails')),
        toEnumFromString(MapUtils.get<string>(map, 'source'), OrderSource),
        toEnumFromString(MapUtils.get<string>(map, 'domain'), OrderDomain),
        ComponentList.deserialize(
            context, MapUtils.getList<SerializedData>(map, 'components', (data) => data as SerializedData),
        ),
        MapUtils.get<boolean>(map, 'isGuestOrder'),
        context.decodeDateOrNull(MapUtils.getOrNull<number>(map, 'cancelledDate')),
        MapUtils.getOrNull<string>(map, 'cancelledBy'),
        Price.decodeOrNull(MapUtils.getOrNull<string>(map, 'applicationFee')),
        toEnumFromStringOrNull(MapUtils.getStringOrNull(map, 'applicationFeeVat'), InvoiceVat),
        MapUtils.getList<OpenAccessPass>(map, 'passes', (value) => context.fromData<OpenAccessPass>(value as SerializedData)),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      '$status': fromEnumAsString(this.status),
      'uniqueId': this.uniqueId,
      'creatorId': this.creatorId,
      'userId': this.userId,
      'customerId': this.customerId,
      'businessRef': this.businessRef.encode(),
      'country': fromEnumAsString(this.country),
      'timezone': fromEnumAsString(this.timezone),
      'creationDate': context.encodeDate(this.creationDate),
      'isGuestOrder': this.isGuestOrder,
      'components': this.components.serialize(context),
      'paymentType': fromEnumAsString(this.paymentType),
      'paymentMethod': fromEnumAsString(this.paymentMethod),
      'onlinePaymentDetails': context.toDataOrNull(this.onlinePaymentDetails),
      'offlinePaymentDetails': context.toDataOrNull(this.offlinePaymentDetails),
      'applicationFee': this.applicationFee?.encode() ?? null,
      'applicationFeeVat': fromEnumAsStringOrNull(this.applicationFeeVat),
      'cancelledDate': context.encodeDateOrNull(this.cancelledDate),
      'cancelledBy': this.cancelledBy,
      'source': fromEnumAsString(this.source),
      'domain': fromEnumAsString(this.domain),
      'passes': this.passes.map((e) => context.toData(e)),
    };
  }

  clone(): this {
    return new OnlineEventPassOrder(
        this.uniqueId,
        this.creatorId,
        TimeZone.cloneDate(this.creationDate),
        this.businessRef.clone(),
        this.country,
        this.timezone,
        this.status,
        this.paymentType,
        this.paymentMethod,
        this.onlinePaymentDetails?.clone() ?? null,
        this.offlinePaymentDetails?.clone() ?? null,
        this.source,
        this.domain,
        this.components.clone(),
        this.isGuestOrder,
        TimeZone.cloneDateOrNull(this.cancelledDate),
        this.cancelledBy,
        this.applicationFee?.clone() ?? null,
        this.applicationFeeVat,
        this.passes.map((e) => e.clone()),
    ) as this;
  }

  isSame(other: OnlineEventPassOrder): boolean {
    if(this === other) {
      return true;
    }

    return isSameData(other.onlinePaymentDetails, this.onlinePaymentDetails) &&
        isSameData(other.offlinePaymentDetails, this.offlinePaymentDetails) &&
        isSameList(other.passes, this.passes, isSameData) &&
        other.components.isSame(this.components) &&
        other.country === this.country &&
        other.timezone === this.timezone &&
        other.source === this.source &&
        other.domain === this.domain &&
        other.uniqueId === this.uniqueId &&
        other.creatorId === this.creatorId &&
        other.cancelledDate === this.cancelledDate &&
        other.cancelledBy === this.cancelledBy &&
        other.applicationFeeVat == this.applicationFeeVat &&
        isSameData(other.applicationFee, this.applicationFee) &&
        isSameData(other.businessRef, this.businessRef) &&
        other.creationDate === this.creationDate &&
        other.status === this.status &&
        other.paymentMethod === this.paymentMethod &&
        other.paymentType === this.paymentType &&
        other.isGuestOrder === this.isGuestOrder;
  }

  copyWithPasses(passes: OpenAccessPass[]): this {
    return new OnlineEventPassOrder(
        this.uniqueId,
        this.creatorId,
        TimeZone.cloneDate(this.creationDate),
        this.businessRef.clone(),
        this.country,
        this.timezone,
        this.status,
        this.paymentType,
        this.paymentMethod,
        this.onlinePaymentDetails?.clone() ?? null,
        this.offlinePaymentDetails?.clone() ?? null,
        this.source,
        this.domain,
        this.components.clone(),
        this.isGuestOrder,
        TimeZone.cloneDateOrNull(this.cancelledDate),
        this.cancelledBy,
        this.applicationFee?.clone() ?? null,
        this.applicationFeeVat,
        passes,
    ) as this;
  }

  copyWithPaymentDetails(onlinePaymentDetails: OnlinePaymentDetails): this {
    return new OnlineEventPassOrder(
        this.uniqueId,
        this.creatorId,
        TimeZone.cloneDate(this.creationDate),
        this.businessRef.clone(),
        this.country,
        this.timezone,
        this.status,
        this.paymentType,
        this.paymentMethod,
        onlinePaymentDetails,
        this.offlinePaymentDetails?.clone() ?? null,
        this.source,
        this.domain,
        this.components.clone(),
        this.isGuestOrder,
        TimeZone.cloneDateOrNull(this.cancelledDate),
        this.cancelledBy,
        this.applicationFee?.clone() ?? null,
        this.applicationFeeVat,
        this.passes.map((e) => e.clone()),
    ) as this;
  }

  copyWithOfflinePaymentDetails(offlinePaymentDetails: OfflinePaymentDetails): this {
    return new OnlineEventPassOrder(
        this.uniqueId,
        this.creatorId,
        TimeZone.cloneDate(this.creationDate),
        this.businessRef.clone(),
        this.country,
        this.timezone,
        this.status,
        this.paymentType,
        this.paymentMethod,
        this.onlinePaymentDetails?.clone() ?? null,
        offlinePaymentDetails,
        this.source,
        this.domain,
        this.components.clone(),
        this.isGuestOrder,
        TimeZone.cloneDateOrNull(this.cancelledDate),
        this.cancelledBy,
        this.applicationFee?.clone() ?? null,
        this.applicationFeeVat,
        this.passes.map((e) => e.clone()),
    ) as this;
  }

  copyWithPaymentStatus(status: PaymentStatus): this {
    return new OnlineEventPassOrder(
        this.uniqueId,
        this.creatorId,
        TimeZone.cloneDate(this.creationDate),
        this.businessRef.clone(),
        this.country,
        this.timezone,
        this.status,
        this.paymentType,
        this.paymentMethod,
        this.onlinePaymentDetails?.copyWith({ status }) ?? null,
        this.offlinePaymentDetails?.clone() ?? null,
        this.source,
        this.domain,
        this.components.clone(),
        this.isGuestOrder,
        TimeZone.cloneDateOrNull(this.cancelledDate),
        this.cancelledBy,
        this.applicationFee?.clone() ?? null,
        this.applicationFeeVat,
        this.passes.map((e) => e.clone()),
    ) as this;
  }

  copyWithPaymentMethod(paymentMethod: PaymentMethod): this {
    return new OnlineEventPassOrder(
        this.uniqueId,
        this.creatorId,
        TimeZone.cloneDate(this.creationDate),
        this.businessRef.clone(),
        this.country,
        this.timezone,
        this.status,
        this.paymentType,
        paymentMethod,
        this.onlinePaymentDetails?.clone() ?? null,
        this.offlinePaymentDetails?.clone() ?? null,
        this.source,
        this.domain,
        this.components.clone(),
        this.isGuestOrder,
        TimeZone.cloneDateOrNull(this.cancelledDate),
        this.cancelledBy,
        this.applicationFee?.clone() ?? null,
        this.applicationFeeVat,
        this.passes.map((e) => e.clone()),
    ) as this;
  }


  assignToCustomerAccount(account: CustomerAccountBase): this {
    return new OnlineEventPassOrder(
        this.uniqueId,
        account.uniqueId,
        TimeZone.cloneDate(this.creationDate),
        this.businessRef.clone(),
        this.country,
        this.timezone,
        this.status,
        this.paymentType,
        this.paymentMethod,
        this.onlinePaymentDetails?.clone() ?? null,
        this.offlinePaymentDetails?.clone() ?? null,
        this.source,
        this.domain,
        this.components.clone(),
        this.isGuestOrder,
        TimeZone.cloneDateOrNull(this.cancelledDate),
        this.cancelledBy,
        this.applicationFee?.clone() ?? null,
        this.applicationFeeVat,
        this.passes.map((e) => e.clone()),
    ) as this;
  }

  tryGetCustomer(): CustomerProfile | null {
    return this.passes[0].customer;
  }

  hasComponentByTypeId(typeId: string): boolean {
    return this.components.hasComponentByTypeId(typeId);
  }

  mustFindComponentByTypeId<C extends Component>(typeId: string): C {
    return this.components.mustFindComponentByTypeId<C>(typeId);
  }

  findComponentByTypeId<C extends Component>(typeId: string): C | null {
    return this.components.findComponentByTypeId<C>(typeId);
  }

  findComponentByInstanceId<C extends Component>(instanceId: string): C | null {
    return this.components.findComponentByInstanceId<C>(instanceId);
  }

  findComponentsByTypeId<C extends Component>(typeId: string): C[] {
    return this.components.findComponentsByTypeId<C>(typeId);
  }

  addComponent(component: Component): void {
    this.components.addComponent(component);
  }

  addComponents(list: Component[]): void {
    this.components.addComponents(list);
  }

  removeComponentByTypeId(typeId: string): boolean {
    return this.components.removeComponentByTypeId(typeId);
  }

  removeComponentByInstanceId(instanceId: string): boolean {
    return this.components.removeComponentByInstanceId(instanceId);
  }

  removeComponentAt(index: number): void {
    this.components.removeComponentAt(index);
  }

  removeAllComponents(): void {
    this.components.removeAllComponents();
  }

  popAllComponentsByTypeId<C extends Component>(typeId: string): C[] {
    return this.components.popAllComponentsByTypeId(typeId);
  }

  popComponentByTypeId<C extends Component>(typeId: string): C | null {
    return this.components.popComponentByTypeId(typeId);
  }

  popComponent<C extends Component>(component: C): C | null {
    return this.components.popComponent(component);
  }

  replaceComponent<C extends Component>(replacement: C): boolean {
    return this.components.replaceComponent(replacement);
  }

  addOrReplaceComponent<C extends Component>(component: C): void {
    this.components.addOrReplaceComponent(component);
  }

  mustFindComponentField<C extends Component, F>(componentTypeId: string, find: (c: C) => F): F {
    return this.components.mustFindComponentField<C, F>(componentTypeId, find);
  }

  findComponentField<C extends Component, F>(componentTypeId: string, find: (c: C) => F): F | null {
    return this.components.findComponentField<C, F>(componentTypeId, find);
  }

  static encodedStatusSafe(status: OnlineEventPassOrderStatus): string {
    return fromEnumAsString(status);
  }

  static verifyType(order: DataModel): boolean {
    return order instanceof OnlineEventPassOrder;
  }

  static assertType(order: DataModel): order is OnlineEventPassOrder {
    return order instanceof OnlineEventPassOrder;
  }
}

export class OnlineEventPassOrderBuilder extends DataModelBuilder<OnlineEventPassOrder> {
  build(context: SerializationContext, map: SerializedData): OnlineEventPassOrder {
    return OnlineEventPassOrder.fromMap(context, map);
  }

  optimizeForQuery(context: SerializationContext, model: OnlineEventPassOrder): SerializedData | null {
    let paymentStatus: PaymentStatus | null = null;

    if(model.paymentType === PaymentType.Online) {
      paymentStatus = model.onlinePaymentDetails?.status ?? null;
    }
    else {
      paymentStatus = PaymentStatus.Succeeded;
    }

    return {
      'paymentStatus': fromEnumAsStringOrNull(paymentStatus),
      'paymentType': fromEnumAsString(model.paymentType),
      'paymentMethod': fromEnumAsString(model.paymentMethod),
    };
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    this.migrateEnum(map, '$status', OnlineEventPassOrderStatus, OnlineEventPassOrderStatus.Open);

    // Needed for deserializing invoices with a graceful creation date! See builder of [OnlinePaymentDetails].
    const creationDate = context.decodeDate(MapUtils.getInteger(map, 'creationDate'));
    context.storage.set('orderCreationDate', creationDate);

    if(versionRange.contains(OnlineEventPassOrder.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(OnlineEventPassOrder.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version parameter `country` of type `Country` was introduced.
    if(version < 2) {
      const timezone = toEnumFromString(MapUtils.get<string>(map, 'timezone'), TimeZoneRegion);
      map['country'] = fromEnumAsString(getCountryOfTimeZone(timezone) ?? Country.Romania);
      version = 2;
    }

    // In this version parameter `domain` of type `OrderDomain` was introduced.
    if(version < 3) {
      map['domain'] = fromEnumAsString(OrderDomain.inTime);
      version = 3;
    }

    // In this version parameter `offlinePaymentDetails` of type `OfflinePaymentDetails | null` was introduced.
    if(version < 4) version = 4;

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
